// Shown on a note that has text versions set aside by a sync conflict (the same words were
// edited on two devices). Lets the user look at each kept version, switch to it, or drop it.
import React, { useState } from 'react';
import type { TextConflictCopy } from '../utils/textMerge';

interface Props {
    copies: TextConflictCopy[];
    language: 'english' | 'vietnamese';
    onRestore: (index: number) => void;
    onDismiss: (index: number) => void;
}

const deviceName = (from: TextConflictCopy['from'], vi: boolean) =>
    from === 'mobile' ? (vi ? 'điện thoại' : 'phone') : from === 'web' ? (vi ? 'web' : 'web') : (vi ? 'thiết bị khác' : 'another device');

export const TextConflictBanner: React.FC<Props> = ({ copies, language, onRestore, onDismiss }) => {
    const vi = language === 'vietnamese';
    const [open, setOpen] = useState<number | null>(null);
    return (
        <div className="mx-4 mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 text-sm">
            <div className="px-3 py-2 text-amber-900 dark:text-amber-200">
                {vi
                    ? `Note này từng bị sửa cùng lúc ở hai nơi. Bản đang hiện là bản mới nhất; ${copies.length} bản kia được giữ lại bên dưới.`
                    : `This note was edited in two places at once. You're seeing the latest version; ${copies.length} other version(s) are kept below.`}
            </div>
            {copies.map((c, i) => (
                <div key={`${c.savedAt}-${i}`} className="border-t border-amber-200 dark:border-amber-800 px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-amber-900 dark:text-amber-200 font-medium">
                            {vi ? 'Bản từ ' : 'Version from '}{deviceName(c.from, vi)} · {new Date(c.savedAt).toLocaleString(vi ? 'vi-VN' : 'en-US')}
                        </span>
                        <div className="ml-auto flex gap-2">
                            <button type="button" className="px-2 py-0.5 rounded border border-amber-400 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-800/40" onClick={() => setOpen(open === i ? null : i)}>
                                {open === i ? (vi ? 'Ẩn' : 'Hide') : (vi ? 'Xem' : 'View')}
                            </button>
                            <button type="button" className="px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700" onClick={() => onRestore(i)}
                                title={vi ? 'Dùng bản này; bản đang hiện sẽ được giữ lại thay thế' : 'Use this version; the current one is kept instead'}>
                                {vi ? 'Dùng bản này' : 'Use this'}
                            </button>
                            <button type="button" className="px-2 py-0.5 rounded text-amber-800 dark:text-amber-300 hover:underline" onClick={() => onDismiss(i)}>
                                {vi ? 'Bỏ' : 'Discard'}
                            </button>
                        </div>
                    </div>
                    {open === i && (
                        <div className="mt-2 max-h-64 overflow-auto rounded bg-white dark:bg-slate-900 p-2 whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                            <div className="font-semibold mb-1">{c.title}</div>
                            {c.content}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
